import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getPunches, recordPunch } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateDdMmmYy } from '../../utils/format';

/**
 * The punch screen for field staff.
 *
 * This is the whole app for someone working on a farm or a site: one button,
 * pressed a few times a day. Everything here follows from where it is used —
 * outdoors, one-handed, on a cheap phone, by someone who is not going to be
 * trained on it and cannot ask anyone what a screen means:
 *
 *   • One button, and the server decides whether it means in or out. Asking
 *     someone standing in a field to pick the right one of two is how you get
 *     a day recorded backwards.
 *   • The button fills the screen. It is the only thing to press.
 *   • The camera and location are gathered before the tap is sent, but never
 *     block it — a punch that fails because the GPS could not get a fix is a
 *     day of pay lost over a technicality.
 *   • Every state says what happened in words, not colour alone.
 */

interface Punch {
  id: number;
  direction: 'IN' | 'OUT';
  punchedAt: string;
  photoPath?: string | null;
  latitude?: string | null;
  longitude?: string | null;
}

interface DayState {
  date: string;
  punches: Punch[];
  firstIn: string | null;
  lastOut: string | null;
  workedMinutes: number;
  nextDirection: 'IN' | 'OUT';
}

const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

const hoursAndMinutes = (mins: number) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (!h) return `${m} min`;
  return m ? `${h} hr ${m} min` : `${h} hr`;
};

/**
 * A selfie from the front camera, as a JPEG blob.
 *
 * Resolved with null on any failure — no camera, permission refused, or the
 * browser simply refusing in a non-secure context. The punch still goes
 * through: proof is valuable, but not at the cost of the attendance record
 * itself.
 */
async function captureSelfie(): Promise<Blob | null> {
  let stream: MediaStream | null = null;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      audio: false,
    });
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    await video.play();

    // One frame is not necessarily ready the instant play() resolves; a short
    // wait avoids saving a black rectangle.
    await new Promise((r) => setTimeout(r, 350));

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);

    return await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.7),
    );
  } catch {
    return null;
  } finally {
    stream?.getTracks().forEach((t) => t.stop());
  }
}

/** Current position, or null. Never rejects, and never waits more than 8s. */
async function currentPosition(): Promise<GeolocationPosition | null> {
  if (!navigator.geolocation) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  });
}

const MobileAttendance: React.FC = () => {
  const { user, logout } = useAuth();
  const [day, setDay] = useState<DayState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'warn'; text: string } | null>(null);
  const [now, setNow] = useState(() => new Date());
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // A visible clock: it tells the worker the phone is awake and what time is
  // about to be recorded, before they commit to the tap.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await getPunches();
      if (mounted.current) setDay(res.data);
    } catch {
      if (mounted.current) setMessage({ kind: 'warn', text: 'Could not load today. Check the signal and pull to refresh.' });
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const punch = async () => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    try {
      // Gathered in parallel: on a weak signal these are the slow part, and
      // nobody should stand holding a phone for eight seconds twice over.
      const [photo, pos] = await Promise.all([captureSelfie(), currentPosition()]);

      const res = await recordPunch(
        {
          latitude: pos?.coords.latitude,
          longitude: pos?.coords.longitude,
          locationAccuracy: pos?.coords.accuracy,
        },
        photo ?? undefined,
      );
      if (!mounted.current) return;
      setDay(res.data);
      setMessage({
        kind: 'ok',
        text: res.data.direction === 'IN' ? 'Checked in. Have a good day.' : 'Checked out. See you tomorrow.',
      });
    } catch (err: any) {
      if (!mounted.current) return;
      const status = err?.response?.status;
      if (status === 409) {
        // The server already has this punch — show its version of the day
        // rather than an error, because nothing actually went wrong.
        if (err.response.data?.punches) setDay(err.response.data);
        setMessage({ kind: 'warn', text: err.response.data?.message ?? 'That punch is already recorded.' });
      } else {
        setMessage({
          kind: 'warn',
          text: 'Could not record that. Check the signal and try again.',
        });
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const goingIn = day?.nextDirection !== 'OUT';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="px-5 pt-6 pb-4">
        <p className="text-sm text-gray-500">{formatDateDdMmmYy(new Date())}</p>
        <h1 className="text-2xl font-semibold text-gray-900">
          {user?.staffName ?? 'Attendance'}
        </h1>
        <p className="text-4xl font-light text-gray-800 mt-3 tabular-nums">
          {now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
        </p>
      </header>

      <main className="flex-1 px-5 flex flex-col">
        <button
          type="button"
          onClick={punch}
          disabled={busy}
          aria-label={goingIn ? 'Check in' : 'Check out'}
          className={[
            'w-full rounded-3xl py-16 text-white text-3xl font-semibold shadow-lg',
            'active:scale-[0.98] transition-transform disabled:opacity-70',
            goingIn ? 'bg-green-600' : 'bg-orange-600',
          ].join(' ')}
        >
          {busy ? 'Please wait…' : goingIn ? 'Check In' : 'Check Out'}
        </button>

        <p className="text-center text-gray-500 text-sm mt-3">
          {busy
            ? 'Taking a photo and finding your location'
            : goingIn
              ? 'Press when you start work'
              : 'Press when you stop work or go for a break'}
        </p>

        {message && (
          <div
            role="status"
            className={[
              'mt-4 rounded-xl px-4 py-3 text-sm',
              message.kind === 'ok'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-amber-50 text-amber-900 border border-amber-200',
            ].join(' ')}
          >
            {message.text}
          </div>
        )}

        <section className="mt-6 mb-8">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-medium text-gray-700">Today</h2>
            <span className="text-sm text-gray-600">
              {day && day.workedMinutes > 0 ? `Worked ${hoursAndMinutes(day.workedMinutes)}` : 'Not started'}
            </span>
          </div>

          {day && day.punches.length > 0 ? (
            <ul className="mt-2 bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
              {day.punches.map((p) => (
                <li key={p.id} className="flex items-center gap-3 px-4 py-3">
                  <span
                    className={[
                      'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                      p.direction === 'IN' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700',
                    ].join(' ')}
                    aria-hidden
                  >
                    {p.direction === 'IN' ? '↓' : '↑'}
                  </span>
                  <span className="flex-1 text-gray-800">
                    {p.direction === 'IN' ? 'Checked in' : 'Checked out'}
                  </span>
                  <span className="text-gray-900 tabular-nums">{timeOf(p.punchedAt)}</span>
                  {p.latitude && <span title="Location recorded" aria-label="Location recorded">📍</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-gray-500 text-sm">No punches yet today.</p>
          )}
        </section>
      </main>

      <footer className="px-5 py-4 border-t border-gray-200">
        <button
          type="button"
          onClick={logout}
          className="text-sm text-gray-500 underline"
        >
          Sign out
        </button>
      </footer>
    </div>
  );
};

export default MobileAttendance;
